import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import {
	applyAssignment,
	assignRole,
	getRoles,
	getUserAssignments,
	previewAssignment,
	removeRole,
	removeRoles,
	toggleUserFlag,
} from "../api/rolesPermissionsApi";
import { ROLES_KEYS } from "./useRoles";
import type {
	AssignmentPreviewRequest,
	AssignRolePayload,
	FlagTogglePayload,
} from "../types/RolesPermissions.type";

export const USER_ASSIGNMENTS_KEY = "rbac-user-assignments";
// FF-W3-3: share 012's canonical roles-list cache key (was "rbac-roles") so 012's clone/rename/delete
// mutations (which invalidate ROLES_KEYS.list()) also refresh the assign wizard/drawer role list.
export const ROLES_KEY = ROLES_KEYS.list();

// `enabled` lets callers on the shared (RBAC-off) user surface skip the RBAC
// assignments request entirely — PRO-413: UserListPage renders in both flag
// states and must not fire this query when RBAC v2 is off.
export const useUserAssignments = ({ enabled = true }: { enabled?: boolean } = {}) => {
	const { data, isLoading, error } = useQuery({
		queryKey: [USER_ASSIGNMENTS_KEY],
		queryFn: getUserAssignments,
		enabled,
	});

	return { users: data ?? [], isLoading, error };
};

// CONTRACT §5 (G4) — `assignableToUserIds` is part of the query key so switching the target
// user set (1..N) refetches rather than reusing a stale filtered list. Omitted = unfiltered.
export const useRoles = (
	assignableToUserIds?: number[],
	options?: {
		/**
		 * D10 — lets a caller that only needs this for a closed/inactive UI
		 * surface (e.g. `canAdd` for a modal that isn't open) skip the request
		 * entirely rather than firing one every render. Defaults to `true`
		 * (existing callers are unaffected).
		 */
		enabled?: boolean;
		/**
		 * CONTRACT §5.1 ADDENDUM — threaded straight through to `getRoles`. Part of the query key
		 * (a different value is a genuinely different server response, and this cache key is
		 * shared with the Role Management admin page's own roles-list invalidations — FF-W3-3 —
		 * so the two must not collide). Defaults to `false` (existing callers are unaffected);
		 * only the direct-add role picker (`AccessModeSelector.tsx`) sets it `true`.
		 */
		excludeEventScoped?: boolean;
	}
) => {
	// Keys on the canonical ROLES_KEYS.list() (FF-W3-3) but keeps 013's own robust unwrap queryFn.
	const { data, isLoading, error } = useQuery({
		queryKey: [...ROLES_KEY, assignableToUserIds ?? null, options?.excludeEventScoped ?? false],
		queryFn: () =>
			getRoles(assignableToUserIds, { excludeEventScoped: options?.excludeEventScoped }),
		enabled: options?.enabled ?? true,
	});

	return { roles: data ?? [], isLoading, error };
};


/**
 * Gap review L2 — a write that changes a user's assignments changes TWO things,
 * and both are on screen at once while `ManageRolesModal` is open:
 *
 *  - the assignments list, i.e. what this user holds (the table), and
 *  - the rule-6 roles list, i.e. what this user can still be GIVEN, which is
 *    what enables or disables Add (`UserListPage`'s `useRoles([userId])`).
 *
 * Refreshing only the first left Add describing a state the server had already
 * moved past: grant someone their last remaining role company-wide and Add
 * stayed enabled; revoke a role and Add stayed disabled. The two paths have to
 * agree, so they share this.
 *
 * `ROLES_KEY` is the BARE list key while the observer's key is
 * `[...ROLES_KEY, userIds]`. That is deliberate, not an oversight: React Query
 * matches by prefix, so this reaches every filtered variant without the mutation
 * needing to know which user set is being watched. A test pins that premise.
 */
const invalidateAssignmentsAndAssignability = (queryClient: QueryClient) =>
	Promise.all([
		queryClient.invalidateQueries({ queryKey: [USER_ASSIGNMENTS_KEY] }),
		queryClient.invalidateQueries({ queryKey: ROLES_KEY }),
	]);

// Mutations invalidate the assignments list so rows reflect server-authoritative state (AC5/AC6).
export const useAssignmentMutations = () => {
	const queryClient = useQueryClient();
	const invalidate = () => invalidateAssignmentsAndAssignability(queryClient);
	// A user FLAG (template_creation) has no bearing on which roles that user may
	// be given, so this one deliberately leaves the roles list alone rather than
	// refetching it on every toggle.
	const invalidateAssignmentsOnly = () =>
		queryClient.invalidateQueries({ queryKey: [USER_ASSIGNMENTS_KEY] });

	const assignRoleMutation = useMutation({
		mutationFn: ({ userId, payload }: { userId: number; payload: AssignRolePayload }) =>
			assignRole(userId, payload),
		onSuccess: invalidate,
	});

	const removeRoleMutation = useMutation({
		mutationFn: ({ userId, assignmentId }: { userId: number; assignmentId: number }) =>
			removeRole(userId, assignmentId),
		onSuccess: invalidate,
	});

	const toggleFlagMutation = useMutation({
		mutationFn: ({ userId, payload }: { userId: number; payload: FlagTogglePayload }) =>
			toggleUserFlag(userId, payload),
		onSuccess: invalidateAssignmentsOnly,
	});

	return {
		assignRoleMutation,
		removeRoleMutation,
		toggleFlagMutation,
	};
};

// CONTRACT §6 (G5) — table-view checkbox-delete (bulk revoke), a DIFFERENT UI moment from
// §11's Add-flow auto-cleanup below; both remove rows, neither supersedes the other.
export const useAssignmentRemovalMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ userId, assignmentIds }: { userId: number; assignmentIds: number[] }) =>
			removeRoles(userId, assignmentIds),
		// Revoking can only widen what this user may still be given, so Add must be
		// allowed to come back on — the mirror of the apply case below (L2).
		onSuccess: () => invalidateAssignmentsAndAssignability(queryClient),
	});
};

// CONTRACT §11 — read-only preview for the confirm dialog. No cache invalidation: this never
// writes. The confirm dialog owns a frozen copy of the request from the moment this resolves.
export const useAssignmentPreview = () =>
	useMutation({
		mutationFn: (payload: AssignmentPreviewRequest) => previewAssignment(payload),
	});

// CONTRACT §11 — re-sends the SAME request body the confirm dialog previewed. Invalidates the
// assignments list so the table/drawer reflect the newly created/removed rows.
export const useAssignmentApply = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: AssignmentPreviewRequest) => applyAssignment(payload),
		// Granting can only narrow it, up to and including "nothing left to add" (L2).
		onSuccess: () => invalidateAssignmentsAndAssignability(queryClient),
	});
};
